import { BriefcaseBusiness, Plus, Trash2, UserRound } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { FeatureFlagTargetPicker } from "@/components/admin/FeatureFlagTargetPicker";
import { useWorkspace } from "@/hooks/useWorkspace";
import { getFeatureFlagSummary } from "@/lib/featureFlagAdmin";
import {
  type FeatureFlagRecord,
  type FeatureRolloutMode,
  rolloutDescriptions,
  rolloutOptions,
} from "./types";
import type { FeatureFlagsAdmin } from "./useFeatureFlagsAdmin";

export function FlagRow({ flag, admin }: { flag: FeatureFlagRecord; admin: FeatureFlagsAdmin }) {
  const { active } = useWorkspace();
  const {
    user, targetBusyKey, targetInputs, flagDrafts, flagActivity,
    workspaceOptions, userOptions, getTargetLabel,
    updateTargetInput, updateDraft, updateFlag, addTarget, addTargetById, removeTarget,
  } = admin;

  const draft = flagDrafts[flag.id] ?? { name: flag.name, description: flag.description ?? "" };
  const userTargets = (flag.feature_flag_targets ?? []).filter((t) => t.target_type === "user");
  const workspaceTargets = (flag.feature_flag_targets ?? []).filter((t) => t.target_type === "workspace");
  const summary = getFeatureFlagSummary({
    enabled: flag.enabled,
    rolloutMode: flag.rollout_mode,
    userCount: userTargets.length,
    workspaceCount: workspaceTargets.length,
  });
  const isToggleSaving = flagActivity.toggleId === flag.id;
  const isRolloutSaving = flagActivity.rolloutId === flag.id;
  const isMetadataSaving = flagActivity.saveId === flag.id;
  const hasUnsavedMetadata = draft.name.trim() !== flag.name || (draft.description ?? "").trim() !== (flag.description ?? "");
  const selectedUserId = targetInputs[flag.id]?.user ?? "";
  const selectedWorkspaceId = targetInputs[flag.id]?.workspace ?? "";
  const selectedUserAlreadyAdded = !!selectedUserId && userTargets.some((t) => t.target_id === selectedUserId);
  const selectedWorkspaceAlreadyAdded = !!selectedWorkspaceId && workspaceTargets.some((t) => t.target_id === selectedWorkspaceId);
  const hasCurrentUser = !!user && userTargets.some((t) => t.target_id === user.id);
  const hasCurrentWorkspace = !!active && workspaceTargets.some((t) => t.target_id === active.id);

  return (
    <div className="rounded-md border border-border bg-background p-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className="font-mono uppercase tracking-wide">{flag.key}</Badge>
            <Badge variant={flag.enabled ? "default" : "outline"}>{flag.enabled ? "Enabled" : "Disabled"}</Badge>
            <Badge variant="outline" className="capitalize">{flag.rollout_mode}</Badge>
          </div>
          <div className="grid gap-3 lg:max-w-2xl lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor={`feature-name-${flag.id}`}>Display name</Label>
                <Input id={`feature-name-${flag.id}`} value={draft.name} onChange={(e) => updateDraft(flag.id, "name", e.target.value)} placeholder="Display name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor={`feature-description-${flag.id}`}>Description</Label>
                <Textarea id={`feature-description-${flag.id}`} rows={3} value={draft.description} onChange={(e) => updateDraft(flag.id, "description", e.target.value)} placeholder="What this flag controls" />
              </div>
            </div>
            <div className="grid gap-2 text-sm lg:min-w-56">
              <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
                <p className="font-medium">Live summary</p>
                <p className="mt-1 text-muted-foreground">{summary}</p>
              </div>
              <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
                <p className="font-medium">Targets</p>
                <p className="mt-1 text-muted-foreground">{userTargets.length} users • {workspaceTargets.length} workspaces</p>
              </div>
              <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-muted-foreground">
                <p className="font-medium text-foreground">Last updated</p>
                <p className="mt-1">{new Date(flag.updated_at).toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-3 lg:min-w-72">
          <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
            <div>
              <p className="text-sm font-medium">Master switch</p>
              <p className="text-xs text-muted-foreground">Turn the flag off instantly without removing targets.</p>
            </div>
            <Switch checked={flag.enabled} onCheckedChange={(c) => updateFlag(flag, { enabled: c })} aria-label={`Toggle ${flag.name}`} disabled={isToggleSaving} />
          </div>
          <div className="space-y-2">
            <Label>Rollout mode</Label>
            <Select value={flag.rollout_mode} onValueChange={(v) => updateFlag(flag, { rollout_mode: v as FeatureRolloutMode })} disabled={isRolloutSaving}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {rolloutOptions.map((option) => (
                  <SelectItem key={option} value={option} className="capitalize">{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{rolloutDescriptions[flag.rollout_mode]}</p>
          </div>
          <Button variant="outline" disabled={!hasUnsavedMetadata || isMetadataSaving} onClick={() => updateFlag(flag, { name: draft.name, description: draft.description })}>
            {isMetadataSaving ? "Saving…" : "Save details"}
          </Button>
          {hasUnsavedMetadata ? (
            <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">Unsaved changes are ready to save.</div>
          ) : (
            <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">Details are in sync with the backend.</div>
          )}
        </div>
      </div>

      <Separator className="my-4" />

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="space-y-3 rounded-md border border-border bg-muted/20 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <Label>User allowlist</Label>
              <p className="text-xs text-muted-foreground">Grant access to individual people before wider rollout.</p>
            </div>
            <Button variant="ghost" size="sm" disabled={!user || hasCurrentUser || targetBusyKey === `${flag.id}:user:add`} onClick={() => user && addTargetById(flag, "user", user.id)}>
              <Plus className="h-4 w-4" /> Add me
            </Button>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <FeatureFlagTargetPicker label="User" placeholder="Select a user" emptyLabel="No matching users" value={targetInputs[flag.id]?.user ?? ""} options={userOptions} onSelect={(v) => updateTargetInput(flag.id, "user", v)} />
            <Button variant="outline" onClick={() => addTarget(flag, "user")} disabled={targetBusyKey === `${flag.id}:user:add` || !selectedUserId || selectedUserAlreadyAdded}>
              {targetBusyKey === `${flag.id}:user:add` ? "Adding…" : "Add user"}
            </Button>
          </div>
          {selectedUserAlreadyAdded && <p className="text-xs text-muted-foreground">That user is already on this allowlist.</p>}
          <div className="grid gap-3">
            {userTargets.length === 0 && <p className="text-sm text-muted-foreground">No individual users yet. Use the picker or add yourself to test quickly.</p>}
            {userTargets.map((target) => (
              <div key={target.id} className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-sm">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 font-medium"><UserRound className="h-4 w-4 text-primary" />{getTargetLabel("user", target.target_id)}</p>
                  <p className="truncate text-muted-foreground">Direct user access</p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => removeTarget(flag, target)} disabled={targetBusyKey === target.id} aria-label="Remove user target">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3 rounded-md border border-border bg-muted/20 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <Label>Workspace allowlist</Label>
              <p className="text-xs text-muted-foreground">Grant access to every member of selected workspaces.</p>
            </div>
            <Button variant="ghost" size="sm" disabled={!active || hasCurrentWorkspace || targetBusyKey === `${flag.id}:workspace:add`} onClick={() => active && addTargetById(flag, "workspace", active.id)}>
              <Plus className="h-4 w-4" /> Add current workspace
            </Button>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <FeatureFlagTargetPicker label="Workspace" placeholder="Select a workspace" emptyLabel="No matching workspaces" value={targetInputs[flag.id]?.workspace ?? ""} options={workspaceOptions} onSelect={(v) => updateTargetInput(flag.id, "workspace", v)} />
            <Button variant="outline" onClick={() => addTarget(flag, "workspace")} disabled={targetBusyKey === `${flag.id}:workspace:add` || !selectedWorkspaceId || selectedWorkspaceAlreadyAdded}>
              {targetBusyKey === `${flag.id}:workspace:add` ? "Adding…" : "Add workspace"}
            </Button>
          </div>
          {selectedWorkspaceAlreadyAdded && <p className="text-xs text-muted-foreground">That workspace is already on this allowlist.</p>}
          <div className="grid gap-3">
            {workspaceTargets.length === 0 && <p className="text-sm text-muted-foreground">No workspaces yet. Add the active workspace or target another one from the picker.</p>}
            {workspaceTargets.map((target) => (
              <div key={target.id} className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-sm">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 font-medium"><BriefcaseBusiness className="h-4 w-4 text-primary" />{getTargetLabel("workspace", target.target_id)}</p>
                  <p className="truncate text-muted-foreground">Workspace-wide access</p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => removeTarget(flag, target)} disabled={targetBusyKey === target.id} aria-label="Remove workspace target">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
